import React from 'react';
import { MenuIcon, Bell, Sun, Moon, User, LogIn, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import SectorToggle from '@/components/ui/SectorToggle';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useTheme } from '@/contexts/ThemeContext';
import LanguageSelector from '@/components/ui/LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';
import { useIsMobile } from '@/hooks/use-mobile';

interface NavbarProps {
  onToggleSidebar?: () => void;
  className?: string;
}

const Navbar = ({ onToggleSidebar, className }: NavbarProps) => {
  const { theme, toggleTheme } = useTheme();
  const { isRTL, getFlexDirection } = useLanguage();
  const { translate } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  
  const handleLogin = () => {
    navigate('/login');
  };
  
  const handleLogout = async () => {
    await logout();
    toast({
      title: translate("common.logoutSuccess"),
      description: translate("common.logoutSuccessMessage"),
    });
    navigate('/login');
  };
  
  const handleProfileClick = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  // Don't show navigation elements for login, register, and sector selection pages
  const noNavPaths = ['/login', '/register', '/select-sector'];
  const showNav = !noNavPaths.includes(location.pathname);
  
  return (
    <header className={cn(
      "h-16 border-b border-border px-4 bg-background/80 backdrop-blur-sm", 
      className
    )}>
      <div className={cn(
        "h-full flex items-center justify-between gap-4 max-w-7xl mx-auto",
        getFlexDirection()
      )}>
        <div className="flex items-center gap-4">
          {onToggleSidebar && showNav && isAuthenticated && (
            <Button 
              id="nav-toggle-button"
              variant="ghost" 
              size="icon" 
              onClick={onToggleSidebar}
              aria-label={translate("common.toggleSidebar")}
            >
              <MenuIcon className="h-5 w-5" />
            </Button>
          )}
          
          <Link to="/" className={cn(
            "flex items-center gap-2", 
            isRTL && "flex-row-reverse"
          )}>
            <img 
              src="/images/logo.png" 
              alt={translate("common.logoAlt")} 
              className="h-8 w-8 object-contain" 
            />
            <span className="font-semibold text-lg hidden sm:inline-block">
              {translate("common.appName")}
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {showNav && isAuthenticated && !isMobile && (
            <SectorToggle showLabels={false} />
          )}
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              aria-label={translate("common.toggleTheme")}
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {showNav && (
              <>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleProfileClick}
                  aria-label={translate("common.profile")}
                >
                  <User className="h-5 w-5" />
                </Button>

                {isAuthenticated ? (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleLogout}
                    aria-label={translate("common.logout")}
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleLogin}
                    aria-label={translate("common.login")}
                  >
                    <LogIn className="h-5 w-5" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
